import { Injectable } from '@angular/core';
import { Camera } from '../models/camera';
import { ConfigService } from './configuration.service';

export interface CameraControlRange {
  min: number;
  max: number;
  step: number;
}

export interface CameraCapabilities {
  zoom?: CameraControlRange;
  focusDistance?: CameraControlRange;
  focusModes: string[];
  supportsZoom: boolean;
  supportsManualFocus: boolean;
  supportsAutoFocus: boolean;
  autoFocusMode?: string;
}

export interface CameraControlSettings {
  zoom?: number;
  focusMode?: string;
  focusDistance?: number;
}

type CameraConstraintSet = MediaTrackConstraintSet & {
  zoom?: number;
  focusMode?: string;
  focusDistance?: number;
};

@Injectable({
  providedIn: 'root'
})
export class CameraService {
  private knownCameraIds = new Set<string>();
  private isEnumeratingCameras = false;
  private enumerateAgainWhenIdle = false;
  private enumerateAgainForceReprobe = false;

  constructor(private configService: ConfigService) {}

  private get controlsStorageKey(): string {
    return `KRUMMYSWINGLAB-${this.configService.environment}-CAMERACONTROLS`;
  }

  async getAvailableCameras(
    inUseDeviceIds: Set<string> = new Set(),
    options?: { forceReprobe?: boolean }
  ): Promise<Camera[]> {
    if (!navigator.mediaDevices) {
      return [];
    }

    if (this.isEnumeratingCameras) {
      this.enumerateAgainWhenIdle = true;
      this.enumerateAgainForceReprobe = this.enumerateAgainForceReprobe || !!options?.forceReprobe;
      return this.listVideoDevices();
    }

    this.isEnumeratingCameras = true;

    try {
      if (options?.forceReprobe) {
        this.knownCameraIds.clear();
      }

      if (this.knownCameraIds.size === 0) {
        await this.requestCameraPermission();
      }

      await this.discoverVideoDevices(inUseDeviceIds);
      return this.listVideoDevices();
    } catch (error) {
      console.error('Error getting available cameras:', error);
      return this.listVideoDevices();
    } finally {
      this.isEnumeratingCameras = false;
      if (this.enumerateAgainWhenIdle) {
        this.enumerateAgainWhenIdle = false;
        const forceReprobe = this.enumerateAgainForceReprobe;
        this.enumerateAgainForceReprobe = false;
        void this.getAvailableCameras(inUseDeviceIds, { forceReprobe });
      }
    }
  }

  getCapabilities(track: MediaStreamTrack): CameraCapabilities {
    const caps = this.readTrackCaps(track);
    const zoom = this.readRange(caps, 'zoom');
    const focusDistance = this.readRange(caps, 'focusDistance');
    const focusModeCap = caps['focusMode'];
    const focusModes = Array.isArray(focusModeCap) ? [...focusModeCap] : [];
    const autoFocusMode = focusModes.includes('continuous')
      ? 'continuous'
      : focusModes.includes('auto')
        ? 'auto'
        : undefined;

    return {
      zoom,
      focusDistance,
      focusModes,
      supportsZoom: !!zoom,
      supportsManualFocus: focusModes.includes('manual') && !!focusDistance,
      supportsAutoFocus: !!autoFocusMode,
      autoFocusMode
    };
  }

  getCurrentControls(track: MediaStreamTrack): CameraControlSettings {
    const settings = track.getSettings() as MediaTrackSettings & CameraControlSettings;
    return {
      zoom: typeof settings.zoom === 'number' ? settings.zoom : undefined,
      focusMode: typeof settings.focusMode === 'string' ? settings.focusMode : undefined,
      focusDistance: typeof settings.focusDistance === 'number' ? settings.focusDistance : undefined
    };
  }

  getSavedControls(deviceId: string): CameraControlSettings {
    const all = this.readAllSavedControls();
    return all[deviceId] ?? {};
  }

  saveControls(deviceId: string, controls: CameraControlSettings): void {
    const all = this.readAllSavedControls();
    all[deviceId] = { ...all[deviceId], ...controls };
    localStorage.setItem(this.controlsStorageKey, JSON.stringify(all));
  }

  async applySavedControls(track: MediaStreamTrack): Promise<void> {
    const deviceId = track.getSettings().deviceId;
    if (!deviceId) {
      return;
    }

    await this.applyControls(track, this.getSavedControls(deviceId));
  }

  async applyControls(track: MediaStreamTrack, controls: CameraControlSettings): Promise<void> {
    const caps = this.getCapabilities(track);

    try {
      if (controls.zoom != null && caps.zoom) {
        await this.applyAdvanced(track, {
          zoom: this.clamp(controls.zoom, caps.zoom.min, caps.zoom.max)
        });
      }

      const requestedFocusMode = controls.focusMode;
      if (requestedFocusMode && caps.focusModes.includes(requestedFocusMode)) {
        await this.applyAdvanced(track, { focusMode: requestedFocusMode });
      }

      const usingManualFocus = (requestedFocusMode ?? this.getCurrentControls(track).focusMode) === 'manual';
      if (usingManualFocus && controls.focusDistance != null && caps.focusDistance) {
        await this.applyAdvanced(track, {
          focusDistance: this.clamp(controls.focusDistance, caps.focusDistance.min, caps.focusDistance.max)
        });
      }
    } catch (error) {
      console.warn('Unable to apply camera controls:', error);
    }
  }

  private async requestCameraPermission() {
    try {
      const permissionStream = await navigator.mediaDevices.getUserMedia({ video: true });
      this.stopTracks(permissionStream);
      await this.yieldMs(250);
    } catch (error) {
      console.warn('Default camera permission request failed; enumerating anyway:', error);
    }
  }

  private async discoverVideoDevices(inUseDeviceIds: Set<string>) {
    let foundNew = true;
    let passes = 0;

    // Chrome on Windows often hides USB cameras until each one has been opened.
    while (foundNew && passes < 8) {
      foundNew = false;
      passes++;

      const videoDevices = (await navigator.mediaDevices.enumerateDevices())
        .filter(device => device.kind === 'videoinput' && device.deviceId);

      for (const device of videoDevices) {
        if (this.knownCameraIds.has(device.deviceId)) {
          continue;
        }

        this.knownCameraIds.add(device.deviceId);
        foundNew = true;

        if (inUseDeviceIds.has(device.deviceId)) {
          continue;
        }

        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: device.deviceId } }
          });
          this.stopTracks(stream);
          await this.yieldMs(150);
        } catch {
          // Busy or unsupported devices should not block discovery of the rest.
        }
      }
    }
  }

  private async listVideoDevices(): Promise<Camera[]> {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return [];
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter(device => device.kind === 'videoinput' && device.deviceId)
      .map((device, index) => ({
        value: device.deviceId,
        label: device.label || `Camera ${index + 1}`
      }));
  }

  private async applyAdvanced(track: MediaStreamTrack, constraints: CameraConstraintSet): Promise<void> {
    await track.applyConstraints({ advanced: [constraints] });
  }

  private readAllSavedControls(): Record<string, CameraControlSettings> {
    const raw = localStorage.getItem(this.controlsStorageKey);
    if (!raw) {
      return {};
    }

    try {
      return JSON.parse(raw) as Record<string, CameraControlSettings>;
    } catch {
      return {};
    }
  }

  private readTrackCaps(track: MediaStreamTrack): Record<string, unknown> {
    const capabilities = track.getCapabilities?.();
    return (capabilities ?? {}) as Record<string, unknown>;
  }

  private readRange(caps: Record<string, unknown>, key: string): CameraControlRange | undefined {
    const range = caps[key] as { min?: number; max?: number; step?: number } | undefined;
    if (!range || typeof range.min !== 'number' || typeof range.max !== 'number' || range.min === range.max) {
      return undefined;
    }

    return {
      min: range.min,
      max: range.max,
      step: range.step && range.step > 0 ? range.step : (range.max - range.min) / 100
    };
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private stopTracks(stream: MediaStream) {
    stream.getTracks().forEach(track => track.stop());
  }

  private yieldMs(ms: number) {
    return new Promise<void>(resolve => setTimeout(resolve, ms));
  }
}
